/**
 * Pricing Configuration for Clear Defense AI
 * 
 * This file centralizes all pricing information to ensure consistency
 * across the application.
 */

// Base monthly prices
export const PLAN_PRICES = {
  trial: {
    monthly: 0,
    annual: 0,
    name: 'Trial',
    description: 'Try Clear Defense AI free for 14 days',
    seatsIncluded: 5,
    extraSeatPrice: {
      monthly: 0,
      annual: 0,
    },
    features: [
      '5 users included',
      'All 4 protection layers',
      'AI-powered threat analysis',
      '14-day trial period',
    ],
  },
  professional: {
    monthly: 299,
    annual: 299 * 10, // 2 months free (10 months for price of 12)
    name: 'Professional',
    description: 'Perfect for small to medium businesses',
    seatsIncluded: 5,
    extraSeatPrice: {
      monthly: 29,
      annual: 29 * 10, // 2 months free per seat
    },
    features: [
      '5 users included',
      'All 4 protection layers',
      'AI-powered threat analysis',
      'Email & chat support',
      'Weekly security reports',
      'Browser extension',
    ],
  },
  enterprise: {
    monthly: 599,
    annual: 599 * 10, // 2 months free
    name: 'Enterprise',
    description: 'For organizations with advanced needs',
    seatsIncluded: 15,
    extraSeatPrice: {
      monthly: 25,
      annual: 25 * 10, // 2 months free per seat
    },
    features: [
      '15 users included',
      'All Professional features',
      'Custom integrations',
      'Dedicated account manager',
      'SLA guarantee',
      'Priority support',
    ],
  },
};

// Calculate effective monthly price for annual billing
export const getEffectiveMonthlyPrice = (plan, billingCycle) => {
  const planConfig = PLAN_PRICES[plan];
  if (!planConfig) return 0;
  
  if (billingCycle === 'annual') {
    return Math.round(planConfig.annual / 12);
  }
  return planConfig.monthly;
};

// Calculate annual savings
export const getAnnualSavings = (plan) => {
  const planConfig = PLAN_PRICES[plan];
  if (!planConfig) return 0;
  
  const monthlyTotal = planConfig.monthly * 12;
  return monthlyTotal - planConfig.annual;
};

// Format price with currency
export const formatPrice = (amount, showCents = false) => {
  if (showCents) {
    return `$${amount.toFixed(2)}`;
  }
  return `$${Math.round(amount)}`;
};

// Calculate total price including extra seats
export const calculateTotalPrice = (plan, billingCycle, extraSeats = 0) => {
  const planConfig = PLAN_PRICES[plan];
  if (!planConfig) return { basePrice: 0, seatsPrice: 0, total: 0 };
  
  const isAnnual = billingCycle === 'annual';
  const basePrice = isAnnual ? planConfig.annual : planConfig.monthly;
  const seatPrice = isAnnual ? planConfig.extraSeatPrice.annual : planConfig.extraSeatPrice.monthly;
  const seatsPrice = extraSeats * seatPrice;
  
  return {
    basePrice,
    seatsPrice,
    total: basePrice + seatsPrice,
    perMonth: isAnnual ? Math.round((basePrice + seatsPrice) / 12) : basePrice + seatsPrice,
  };
};

// Mixed billing calculation (annual plan + monthly users)
export const calculateMixedBilling = (plan, planBillingCycle, extraSeats, seatsBillingCycle) => {
  const planConfig = PLAN_PRICES[plan];
  if (!planConfig) return { planCost: 0, seatsCost: 0, total: 0 };
  
  // Plan cost
  const planCost = planBillingCycle === 'annual' ? planConfig.annual : planConfig.monthly;
  const planPeriod = planBillingCycle === 'annual' ? 12 : 1;
  
  // Seats cost
  const seatPrice = seatsBillingCycle === 'annual' 
    ? planConfig.extraSeatPrice.annual 
    : planConfig.extraSeatPrice.monthly;
  const seatsCost = extraSeats * seatPrice;
  const seatsPeriod = seatsBillingCycle === 'annual' ? 12 : 1;
  
  return {
    planCost,
    planPeriod,
    seatsCost,
    seatsPeriod,
    totalMonthly: (planCost / planPeriod) + (seatsCost / seatsPeriod),
  };
};

// Plan comparison helper
export const getPlanFeatures = (plan) => {
  return PLAN_PRICES[plan]?.features || [];
};

export default PLAN_PRICES;
